import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTimeContext } from "./DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, Clock, AlertTriangle, CheckCircle2, Building2, ChevronDown, Calendar } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { PeriodBadge } from "@/components/Formatters";
import ExpectedCashflow from "@/components/ExpectedCashflow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => { if (!n) return '--'; const a = Math.abs(n); const s = n < 0 ? '-' : ''; if (a >= 1e7) return `${s}${(a/1e7).toFixed(2)} Cr`; if (a >= 1e5) return `${s}${(a/1e5).toFixed(2)} L`; return `${s}₹${Math.round(a).toLocaleString('en-IN')}`; };
const fmtDate = (d) => { if (!d) return '-'; const s = d.slice(0, 10); const [y, m, dd] = s.split('-'); return `${dd}-${m}-${y}`; };

function daysBetween(a, b) {
  if (!a || !b) return null;
  try { return Math.round((new Date(b) - new Date(a)) / 86400000); } catch { return null; }
}

function stageDeviation(s) {
  const today = new Date().toISOString().slice(0, 10);
  const revised = s.revised_date?.slice(0, 10);
  const payDue = s.pay_due_date?.slice(0, 10);
  const expected = s.expected_date?.slice(0, 10);

  if (payDue && revised) {
    // Completed: compare payDueDate vs revised
    const dev = daysBetween(revised, payDue);
    if (dev > 0) return { value: dev, label: `+${dev}d late`, type: 'late' };
    if (dev === 0) return { value: 0, label: 'On time', type: 'ontime' };
    return { value: Math.abs(dev), label: `${Math.abs(dev)}d early`, type: 'early' };
  }
  if (!payDue && revised) {
    if (revised < today) {
      // Overdue: revised date is in past, not completed
      const overdue = daysBetween(revised, today);
      return { value: overdue, label: `${overdue}d overdue`, type: 'overdue' };
    } else {
      // Upcoming: revised date is in future
      const daysLeft = daysBetween(today, revised);
      return { value: daysLeft, label: `${daysLeft}d to go`, type: 'upcoming' };
    }
  }
  if (payDue && !revised && expected) {
    // Has payDue but no revised — compare with expected
    const dev = daysBetween(expected, payDue);
    if (dev > 0) return { value: dev, label: `+${dev}d late`, type: 'late' };
    if (dev === 0) return { value: 0, label: 'On time', type: 'ontime' };
    return { value: Math.abs(dev), label: `${Math.abs(dev)}d early`, type: 'early' };
  }
  return { value: null, label: '-', type: 'unknown' };
}

function devBadgeColor(type) {
  if (type === 'early' || type === 'ontime') return 'bg-emerald-100 text-emerald-700';
  if (type === 'upcoming') return 'bg-blue-100 text-blue-700';
  if (type === 'late' || type === 'overdue') return 'bg-red-100 text-red-700';
  return 'bg-slate-100 text-slate-500';
}

export default function CashflowConstruction() {
  const { period, refDate, token, currentMonthInfo } = useTimeContext();
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProjects, setExpandedProjects] = useState({});

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios.get(`${API}/data/fact_completion_stages`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setStages(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module constants, setters are stable
  }, [token]);

  // Group by project
  const projects = {};
  stages.forEach(s => {
    const pn = s.project_name || 'Unknown';
    if (!projects[pn]) projects[pn] = { completed: [], upcoming: [], total: 0, deviations: [] };
    projects[pn].total++;
    if (s.completed) { projects[pn].completed.push(s); }
    else { projects[pn].upcoming.push(s); }
    // Deviation: revised vs expected
    const dev = daysBetween(s.expected_date, s.revised_date);
    if (dev !== null) projects[pn].deviations.push(dev);
  });

  const projectList = Object.entries(projects).map(([name, d]) => {
    const avgDev = d.deviations.length > 0 ? Math.round(d.deviations.reduce((a, b) => a + b, 0) / d.deviations.length) : 0;
    const maxDev = d.deviations.length > 0 ? Math.max(...d.deviations) : 0;
    const pctDone = d.total > 0 ? Math.round(d.completed.length / d.total * 100) : 0;
    // Current stage = first upcoming
    const currentStage = d.upcoming[0] || null;
    // Urgency: stages within 30 days
    const urgent = d.upcoming.filter(s => { const days = daysBetween(new Date().toISOString().slice(0, 10), s.revised_date); return days !== null && days >= 0 && days <= 30; });
    // Health score (0-10)
    let health = 10;
    if (avgDev > 60) health -= 4; else if (avgDev > 30) health -= 2; else if (avgDev > 15) health -= 1;
    if (pctDone < 20) health -= 2; else if (pctDone < 50) health -= 1;
    if (urgent.length > 2) health -= 1;
    health = Math.max(0, Math.min(10, health));
    return { name, ...d, avgDev, maxDev, pctDone, currentStage, urgent, health };
  }).sort((a, b) => a.health - b.health);

  const totalCompleted = stages.filter(s => s.completed).length;
  const totalUpcoming = stages.filter(s => !s.completed).length;
  const allDevs = projectList.flatMap(p => p.deviations);
  const avgDevAll = allDevs.length > 0 ? Math.round(allDevs.reduce((a, b) => a + b, 0) / allDevs.length) : 0;
  const urgentAll = projectList.reduce((s, p) => s + p.urgent.length, 0);

  // Deviation distribution for chart
  const devBuckets = { 'On Time': 0, '1-15d': 0, '16-30d': 0, '31-60d': 0, '60+d': 0 };
  allDevs.forEach(d => {
    if (d <= 0) devBuckets['On Time']++;
    else if (d <= 15) devBuckets['1-15d']++;
    else if (d <= 30) devBuckets['16-30d']++;
    else if (d <= 60) devBuckets['31-60d']++;
    else devBuckets['60+d']++;
  });
  const devChartData = Object.entries(devBuckets).map(([k, v]) => ({ range: k, count: v }));
  const DEV_COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#F97316', '#EF4444'];

  return (
    <div className="space-y-4 sm:space-y-6 overflow-hidden" data-testid="cashflow-construction-page">
      <div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>Engineering Milestone</h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Construction progress, schedule analysis, and expected monthly collection</p>
        <PeriodBadge period={period} currentMonthInfo={currentMonthInfo} />
      </div>

      {loading && <div className="text-center py-8"><div className="inline-block h-6 w-6 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" /></div>}

      {/* Expected Cashflow Forecast */}
      <ExpectedCashflow token={token} refDate={refDate} />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 sm:gap-3">
        <div className="kpi-card"><p className="text-[10px] font-semibold text-emerald-500 uppercase">Completed</p><p className="text-lg font-bold text-emerald-600 mt-1" style={{ fontFamily: 'Manrope' }}>{totalCompleted}</p><p className="text-[9px] text-slate-400">of {stages.length} stages</p></div>
        <div className="kpi-card"><p className="text-[10px] font-semibold text-blue-500 uppercase">Upcoming</p><p className="text-lg font-bold text-blue-600 mt-1" style={{ fontFamily: 'Manrope' }}>{totalUpcoming}</p><p className="text-[9px] text-slate-400">remaining stages</p></div>
        <div className="kpi-card"><p className="text-[10px] font-semibold text-amber-500 uppercase">Avg Deviation</p><p className={`text-lg font-bold mt-1 ${avgDevAll > 30 ? 'text-red-600' : avgDevAll > 15 ? 'text-amber-600' : 'text-emerald-600'}`} style={{ fontFamily: 'Manrope' }}>{avgDevAll} days</p><p className="text-[9px] text-slate-400">revised vs expected</p></div>
        <div className="kpi-card"><p className="text-[10px] font-semibold text-red-500 uppercase">Urgent</p><p className="text-lg font-bold text-red-600 mt-1" style={{ fontFamily: 'Manrope' }}>{urgentAll}</p><p className="text-[9px] text-slate-400">due within 30 days</p></div>
        <div className="kpi-card"><p className="text-[10px] font-semibold text-slate-400 uppercase">Projects</p><p className="text-lg font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope' }}>{projectList.length}</p><p className="text-[9px] text-slate-400">active construction</p></div>
      </div>

      {/* Deviation Chart + Project Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
          <CardHeader className="pb-2 border-b border-slate-100"><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Schedule Deviation Distribution</CardTitle></CardHeader>
          <CardContent className="pt-3">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={devChartData} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="range" tick={{ fontSize: 10, fill: '#475569' }} />
                <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} />
                <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '11px' }} />
                <Bar dataKey="count" name="Stages" radius={[6, 6, 0, 0]}>{devChartData.map((_, i) => <Cell key={i} fill={DEV_COLORS[i]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
          <CardHeader className="pb-2 border-b border-slate-100"><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Project Health Scores</CardTitle></CardHeader>
          <CardContent className="pt-3">
            <div className="space-y-2">
              {projectList.map((p, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50">
                  <span className={`text-sm font-bold w-8 text-center rounded-md py-0.5 ${p.health >= 7 ? 'bg-emerald-100 text-emerald-700' : p.health >= 5 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{p.health}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate">{p.name}</p>
                    <p className="text-[9px] text-slate-400">{p.pctDone}% done · Avg delay: {p.avgDev}d · {p.urgent.length} urgent</p>
                  </div>
                  <div className="h-1.5 w-20 bg-slate-200 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${p.pctDone}%`, background: p.health >= 7 ? '#10B981' : p.health >= 5 ? '#F59E0B' : '#EF4444' }} /></div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Per-Project Construction Detail */}
      {projectList.map((p, pi) => (
        <Card key={pi} className="bg-white rounded-2xl border-slate-200/60 shadow-sm overflow-hidden">
          <button onClick={() => setExpandedProjects(prev => ({ ...prev, [p.name]: !prev[p.name] }))} className="w-full">
            <CardHeader className="pb-2 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-blue-600" /><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>{p.name}</CardTitle></div>
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${p.health >= 7 ? 'bg-emerald-100 text-emerald-700' : p.health >= 5 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>Health: {p.health}/10</span>
                  <span className="text-[10px] text-slate-400">{p.completed.length}/{p.total}</span>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expandedProjects[p.name] ? 'rotate-180' : ''}`} />
                </div>
              </div>
            </CardHeader>
          </button>
          {expandedProjects[p.name] && (
          <CardContent className="pt-3">
            {/* Progress bar */}
            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden mb-3 flex">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${p.pctDone}%` }} />
              {p.currentStage && <div className="h-full bg-blue-500 w-[3%] animate-pulse" />}
            </div>
            {/* Stage timeline */}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-slate-200">
                  <th className="text-left py-1.5 px-2 font-semibold text-slate-600 min-w-[140px]">Stage</th>
                  <th className="text-center py-1.5 px-2 font-semibold text-slate-600 whitespace-nowrap">Revised</th>
                  <th className="text-center py-1.5 px-2 font-semibold text-slate-600 whitespace-nowrap">Completed</th>
                  <th className="text-center py-1.5 px-2 font-semibold text-slate-600 whitespace-nowrap">Dev.</th>
                  <th className="text-center py-1.5 px-2 font-semibold text-slate-600">Status</th>
                </tr></thead>
                <tbody>
                  {[...p.completed, ...p.upcoming].map((s, si) => {
                    const dev = stageDeviation(s);
                    const isCurrent = !s.completed && si === p.completed.length;
                    return (
                      <tr key={si} className={`border-b border-slate-100 ${s.completed ? 'bg-emerald-50/30' : isCurrent ? 'bg-blue-50/50' : ''}`}>
                        <td className="py-1.5 px-2 text-slate-700">{s.event}</td>
                        <td className="py-1.5 px-2 text-center text-slate-700 whitespace-nowrap">{fmtDate(s.revised_date)}</td>
                        <td className="py-1.5 px-2 text-center text-emerald-600 whitespace-nowrap">{fmtDate(s.pay_due_date)}</td>
                        <td className="py-1.5 px-2 text-center">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${devBadgeColor(dev.type)}`}>{dev.label}</span>
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          {s.completed ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 inline" /> : isCurrent ? <Clock className="h-3.5 w-3.5 text-blue-500 inline animate-pulse" /> : <Calendar className="h-3.5 w-3.5 text-slate-300 inline" />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
}
